import React, { useState, useEffect, useRef, useMemo } from 'react';
import { startOfMonth, endOfMonth, addMonths, subMonths, subDays, addDays, format } from 'date-fns';
import { it } from 'date-fns/locale';
import { Search, Calendar as CalendarIcon, Sidebar as SidebarIcon, MapPin, X, Check, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarView } from './CalendarView';
import { ResponsiveModal } from '../ui/ResponsiveModal';
import { CalendarEventDrawer } from './CalendarEventDrawer';
import { EventFormDrawer } from './EventFormDrawer';
import { DealActivityForm } from '../deals/DealActivityForm';
import { TeamAvailabilityView, type TeamSlot } from './team/TeamAvailabilityView';
import { useCalendarSharingStore } from '../../lib/stores/calendarSharingStore';
import { useCalendarStore, calendarStore } from '../../lib/stores/calendarStore';
import { useApp } from '../AppProvider';
import type { CalendarEvent } from '../../lib/types';
import { translations } from '../../lib/i18n/translations';
import { usePermissions } from '../../lib/hooks/usePermissions';
import type { CalendarSource } from './CalendarSourcesSidebar';
import './calendar-modal.css';

// ============================================
// DROPDOWN SELEZIONE CALENDARI
// ============================================

function CalendarSourcesDropdown({
  calendarSources,
  onToggle,
  isLoading,
  loadingCalendarId
}: {
  calendarSources: CalendarSource[];
  onToggle: (id: string) => void;
  isLoading?: boolean;
  loadingCalendarId?: string | null;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Chiudi dropdown cliccando fuori
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const enabledCount = calendarSources.filter(s => s.enabled).length;
  const totalCount = calendarSources.length;

  const typeLabels: Record<string, string> = {
    crm: 'Calendario CRM',
    sales_activities: 'Attività di Vendita',
    project_tasks: 'Task Progetti',
    google: 'Google Calendar'
  };

  // Indicatore di caricamento inline
  const LoadingDots = () => (
    <div className="flex space-x-0.5 ml-auto">
      <div className="w-1 h-1 bg-blue-500 rounded-full animate-bounce" />
      <div className="w-1 h-1 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
      <div className="w-1 h-1 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
    </div>
  );

  return (
    <div className="relative" ref={dropdownRef}>
      <button
                  type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-black/70 dark:text-white/70 hover:text-black dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors"
      >
        <CalendarIcon className="h-4 w-4" />
        <span className="hidden sm:inline">Calendari</span>
        <span className="text-xs bg-black/10 dark:bg-white/10 px-1.5 py-0.5 rounded">
          {enabledCount}/{totalCount}
        </span>
        <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-gray-800 border border-black/10 dark:border-white/10 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
          {/* Header */}
          <div className="px-3 py-2 border-b border-black/10 dark:border-white/10">
            <span className="text-xs font-semibold text-black/60 dark:text-white/60 uppercase">
              Seleziona Calendari
            </span>
          </div>

          {/* Sezioni per tipo */}
          {(['crm', 'sales_activities', 'project_tasks', 'alfadocs', 'google'] as const).map(type => {
            const sources = calendarSources.filter(s => s.type === type);
            if (sources.length === 0) return null;

            return (
              <div key={type} className="p-2">
                <div className="text-[10px] font-semibold uppercase text-black/50 dark:text-white/50 px-1 mb-1">
                  {typeLabels[type]}
                </div>
                {sources.map(source => {
                  const isThisLoading = loadingCalendarId === source.id;
                  return (
                    <label
                      key={source.id}
                      className={`flex items-center gap-2 px-2 py-1.5 hover:bg-black/5 dark:hover:bg-white/5 rounded cursor-pointer ${isThisLoading ? 'opacity-70' : ''}`}
                      onClick={() => !isThisLoading && onToggle(source.id)}
                    >
                      <div
                        className={`w-3 h-3 rounded flex items-center justify-center flex-shrink-0 ${
                          source.enabled ? '' : 'border-2 border-gray-300 dark:border-gray-600'
                        }`}
                        style={source.enabled ? { backgroundColor: source.color } : {}}
                      >
                        {source.enabled && <Check className="w-2 h-2 text-white" strokeWidth={3} />}
                      </div>
                      <span className={`text-sm flex-1 truncate ${source.enabled ? 'text-black/80 dark:text-white/80' : 'text-black/50 dark:text-white/50'}`}>
                        {source.name}
                      </span>
                      {isThisLoading && <LoadingDots />}
                    </label>
                  );
                })}
              </div>
            );
          })}

          {/* Loading indicator generale per Google (caricamento iniziale) */}
          {isLoading && !loadingCalendarId && (
            <div className="px-3 py-2 text-xs text-blue-600 flex items-center gap-2 border-t border-black/10 dark:border-white/10">
              <div className="flex space-x-0.5">
                <div className="w-1 h-1 bg-blue-500 rounded-full animate-bounce" />
                <div className="w-1 h-1 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-1 h-1 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
              Caricamento...
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// CALENDAR MODAL
// ============================================

interface CalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  size?: 'xl' | 'full';
  mode?: 'modal' | 'sidepanel';
  defaultView?: string;
  onSlotSelect?: (start: Date, end: Date, allDay: boolean) => void;
  highlightEventId?: string;
  hideEventId?: string; // ID evento da nascondere (es. evento Google duplicato durante edit)
  onEventDrop?: (eventId: string, newStart: Date, newEnd: Date, allDay: boolean) => void;
  onEventResize?: (eventId: string, newStart: Date, newEnd: Date) => void;
  // Callback per confermare selezione (chiamato quando utente clicca Salva)
  onConfirmSelection?: (start: Date, end: Date) => void;
  // Props per evento sintetico arancione (riflette lo stato del drawer in tempo reale)
  slotTitle?: string;
  slotDateTime?: Date | null;
  slotDuration?: number;
  /** Clic su un buco libero della vista Team: orario e persona (chi apre il pannello imposta data, ora e assegnatario). Senza, la vista Team e' in sola lettura. */
  onTeamPick?: (slot: TeamSlot) => void;
  /** All'apertura: true = parte dalla vista Team (se vedo altri calendari), false = dal calendario. Assente = come era. */
  initialTeam?: boolean;
}

export function CalendarModal({ isOpen, onClose, title = "Il tuo Calendario", size = "full", mode = "modal", defaultView, onSlotSelect, highlightEventId, hideEventId, onEventDrop, onEventResize, onConfirmSelection, slotTitle, slotDateTime, slotDuration, onTeamPick, initialTeam }: CalendarModalProps) {
  // View and navigation state
  const [currentViewRange, setCurrentViewRange] = useState<{start: Date, end: Date} | null>(null);
  const [currentView, setCurrentView] = useState<string>(defaultView || 'timeGridWeek');
  const [sidebarOpen, setSidebarOpen] = useState(false); // Sidebar destra (eventi) - chiusa
  // Sidebar sinistra (calendari) - sempre chiusa nel modale (usiamo dropdown)

  // Stato interno per la selezione pendente (gestito localmente per evitare re-render del parent)
  const [localPendingSelection, setLocalPendingSelection] = useState<{start: Date, end: Date} | null>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  // Event details and editing state
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [showSalesActivityForm, setShowSalesActivityForm] = useState(false);
  // Vista Team compatta al posto del calendario (solo se vedo anche altri calendari)
  const [teamMode, setTeamMode] = useState(false);
  const canShowTeam = useCalendarSharingStore(s => Object.keys(s.visible).length > 1);
  // Vista di partenza scelta da chi apre il pannello (una volta per apertura)
  useEffect(() => {
    if (isOpen && initialTeam !== undefined) setTeamMode(initialTeam && canShowTeam);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track which calendar is currently loading (for dropdown indicator)
  const [loadingCalendarId, setLoadingCalendarId] = useState<string | null>(null);

  // Ref per tracciare se la navigazione è stata iniziata dall'utente
  const userNavigationRef = useRef(false);

  // Debounce refs per evitare troppe chiamate quando si toggano velocemente più calendari
  const dbUpdateDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const fetchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const pendingGoogleEnableRef = useRef(false); // Traccia se stiamo abilitando un calendario Google

  const {
    events,
    googleEvents,
    salesActivities,
    projectTasks,
    loading,
    error,
    fetchEvents,
    createEvent,
    updateEvent,
    deleteEvent,
    calendarSources,
    setCalendarSourceEnabled,
    getEnabledEvents,
    fetchSalesActivities,
    fetchProjectTasks,
    salesActivitiesLoading,
    projectTasksLoading,
    salesActivitiesError,
    projectTasksError,
    googleLoading,
    clearSyncToken,
    fetchGoogleEvents, // Per chiamare direttamente con forceRefresh
    fetchAlfaDocsAppointments,
    alfadocsEvents
  } = useCalendarStore();

  const { toast, confirm, user } = useApp();
  const { canAccessDeals, canEditEvent, canDeleteEvent } = usePermissions();

  // Get translations
  const t = translations[user?.language || 'it'].calendar;

  // Refer to calendar with useRef
  const calendarRef = useRef<any>(null);

  // Track if initial fetch has been done
  const initialFetchRef = useRef(false);

  // Memoize enabled events per evitare reset degli eventi durante drag
  // Senza questo, ogni re-render (es. setLocalPendingSelection) crea un nuovo array
  // che causa il reset della posizione degli eventi in FullCalendar
  // Filtriamo anche hideEventId qui, PRIMA di passare a CalendarView
  const memoizedEvents = useMemo(() => {
    const allEvents = getEnabledEvents();

    let filtered = allEvents;
    if (hideEventId) {
      // hideEventId può essere l'id Google pulito (righe normalizzate) o quello
      // legacy col prefisso 'google-': confronta la forma normalizzata di entrambi
      const normalizedHideId = hideEventId.replace(/^google-/, '');
      filtered = allEvents.filter(event => event.id.replace(/^google-/, '') !== normalizedHideId);
    }

    // Evento sintetico arancione: riflette lo stato dell'attività nel drawer in tempo reale
    if (slotDateTime && slotDuration) {
      const slotEnd = new Date(slotDateTime.getTime() + slotDuration * 60 * 1000);
      const slotEvent = {
        id: 'pending-activity',
        title: slotTitle?.trim() || 'Nuova attività',
        start_time: slotDateTime.toISOString(),
        end_time: slotEnd.toISOString(),
        all_day: false,
        color: '#F97316',
        is_editable: false,
        source: 'pending-activity' as any
      };
      return [...filtered, slotEvent];
    }

    return filtered;
  }, [events, googleEvents, salesActivities, projectTasks, alfadocsEvents, calendarSources, hideEventId, slotDateTime, slotDuration, slotTitle]);

  // Fetch events for a specific date range
  const fetchEventsForDateRange = async (start: Date, end: Date) => {
    try {
      // Add 7-day buffer to improve navigation performance
      const bufferStart = subDays(start, 7);
      const bufferEnd = addDays(end, 7);

      // Fetch CRM events, sales activities, and project tasks in parallel
      // fetchEvents chiamerà automaticamente fetchGoogleEvents dopo 500ms
      await Promise.all([
        fetchEvents(bufferStart, bufferEnd),
        fetchSalesActivities(bufferStart, bufferEnd),
        fetchProjectTasks(bufferStart, bufferEnd),
        fetchAlfaDocsAppointments(bufferStart, bufferEnd)
      ]);

      // NOTA: Non chiamiamo fetchGoogleEvents qui - viene chiamato automaticamente
      // da fetchEvents dopo un delay di 500ms (vedi calendarStore.ts linea 329)
    } catch (err) {
      console.error('Error fetching events:', err);
      toast.error('Failed to load calendar events');
    }
  };

  // Function to directly update Google Calendar IDs with a given array
  const updateGoogleCalendarIdsWithArray = async (googleCalendarIds: string[]) => {
    try {
      const { supabase } = await import('../../lib/supabase');
      const { data: userData } = await supabase.auth.getUser();

      if (userData?.user?.id) {
/*         console.log('[CALENDAR MODAL] Updating user profile with selected Google calendars:', JSON.stringify(googleCalendarIds));
 */
        const { data, error } = await supabase
          .from('user_profiles')
          .update({
            google_calendar_ids: googleCalendarIds || []
          })
          .eq('id', userData.user.id)
          .select();

        if (error) {
          console.error('[CALENDAR MODAL] Error updating user profile with Google calendars:', error);
        } else {
/*           console.log('[CALENDAR MODAL] Successfully updated user profile with selected Google calendars:', data);
 */
          // Clear sync token and reset cooldown ONLY after successful update
          clearSyncToken();
          (calendarStore as any).lastGoogleFetchTime = 0;
        }
      }
    } catch (error) {
      console.error('[CALENDAR MODAL] Failed to update user profile with Google calendars:', error);
    }
  };

  // Initial fetch when modal opens
  useEffect(() => {
    if (isOpen && !initialFetchRef.current) {
      const today = new Date();
      const dayOfWeek = today.getDay() === 0 ? 6 : today.getDay() - 1; // Monday = 0
      const start = subDays(today, dayOfWeek); // Start of current week
      const end = addDays(start, 6); // End of current week

      fetchEventsForDateRange(start, end);
      setCurrentViewRange({ start, end });
      initialFetchRef.current = true;

      // Toast informativo in sidepanel
      if (isSidePanel && onConfirmSelection) {
        toast.info('Seleziona uno slot per impostare data e ora');
      }
    }
  }, [isOpen]);

  // Reset initial fetch flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      initialFetchRef.current = false;
    }
  }, [isOpen]);

  // Reset local pending selection when modal closes
  useEffect(() => {
    if (!isOpen) {
      setLocalPendingSelection(null);
    }
  }, [isOpen]);

  // Load saved Google calendar IDs when modal opens (only once)
  useEffect(() => {
    if (!isOpen) return;

    const loadSavedCalendarIDs = async () => {
      try {
        // Get current user's profile with google_calendar_ids
        const { supabase } = await import('../../lib/supabase');
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) return;

        const { data: profile, error } = await supabase
          .from('user_profiles')
          .select('google_calendar_ids, google_account_connected')
          .eq('id', userData.user.id)
          .single();

        if (error || !profile) {
          console.error('[CALENDAR MODAL] Error loading user profile:', error);
          return;
        }

        // Ensure we have a valid array, even if empty
        const savedCalendarIds = Array.isArray(profile.google_calendar_ids)
          ? profile.google_calendar_ids
          : [];

/*         console.log('[CALENDAR MODAL] Loaded saved Google calendar IDs:', savedCalendarIds);
 */
        // Update the enabled status of Google calendar sources if we have any
        if (calendarSources.some(s => s.type === 'google')) {
          // Update all sources in one state change
          const updatedCalendarSources = calendarSources.map(source => {
            if (source.type === 'google' && source.googleCalendarId) {
              const shouldBeEnabled = savedCalendarIds.includes(source.googleCalendarId);
              return {
                ...source,
                enabled: shouldBeEnabled
              };
            }
            return source;
          });

          // Use the calendarStore directly to set the updated sources
          const { calendarStore } = await import('../../lib/stores/calendarStore');
          calendarStore.setState({ calendarSources: updatedCalendarSources });

/*           console.log('[CALENDAR MODAL] Synchronized calendar sources with saved preferences');
 */        }
      } catch (error) {
        console.error('[CALENDAR MODAL] Error loading saved calendar IDs:', error);
      }
    };

    loadSavedCalendarIDs();
  }, [isOpen]); // Solo quando il modale si apre, NON quando calendarSources cambia

  // Handle view change (when user navigates dates)
  const handleViewChange = (start: Date, end: Date, view: string) => {
    setCurrentViewRange({ start, end });
    fetchEventsForDateRange(start, end);
  };

  // Reset loadingCalendarId when googleLoading finishes
  const prevGoogleLoadingRef = useRef(googleLoading);
  useEffect(() => {
    // Se il caricamento era in corso e ora è finito, resetta l'indicatore
    if (prevGoogleLoadingRef.current && !googleLoading) {
      setLoadingCalendarId(null);
    }
    prevGoogleLoadingRef.current = googleLoading;
  }, [googleLoading]);

  // Toast di completamento quando la navigazione finisce
  const prevLoadingStateRef = useRef(loading || googleLoading);
  useEffect(() => {
    const isLoading = loading || googleLoading;
    // Se stava caricando e ora ha finito, E era una navigazione utente
    if (prevLoadingStateRef.current && !isLoading && userNavigationRef.current) {
      toast.success('Eventi caricati');
      userNavigationRef.current = false;
    }
    prevLoadingStateRef.current = isLoading;
  }, [loading, googleLoading, toast]);

  // Handle calendar source toggle
  // DEBOUNCE: Raggruppa click rapidi per evitare troppe chiamate di rete
  const handleCalendarSourceToggle = (id: string) => {
    const source = calendarSources.find(s => s.id === id);
    if (!source) return;

    // Update the calendar source in the store (questo è istantaneo, nessun debounce)
    const newEnabled = !source.enabled;
    setCalendarSourceEnabled(id, newEnabled);

    // Only update Google calendar IDs if we toggled a Google source
    if (source.type === 'google') {
      // Se abilitiamo un calendario, mostra l'indicatore di caricamento per quel calendario
      if (newEnabled) {
        setLoadingCalendarId(id);
        pendingGoogleEnableRef.current = true; // Traccia che stiamo abilitando
      }

      // ========== DEBOUNCE: DB Update ==========
      // Cancella il timeout precedente se esiste
      if (dbUpdateDebounceRef.current) {
        clearTimeout(dbUpdateDebounceRef.current);
      }

      // Attendi 500ms prima di salvare nel DB (raggruppa click rapidi)
      dbUpdateDebounceRef.current = setTimeout(() => {
        // Usa getState() per ottenere lo stato più recente
        const updatedSources = useCalendarStore.getState().calendarSources;
        const googleCalendarIds = updatedSources
          .filter(s => s.type === 'google' && s.googleCalendarId && s.enabled)
          .map(s => s.googleCalendarId)
          .filter(calId => calId !== undefined) as string[];

        // Save the updated IDs
        updateGoogleCalendarIdsWithArray(googleCalendarIds);
        dbUpdateDebounceRef.current = null;
      }, 500);

      // ========== DEBOUNCE: Fetch Google Events con forceRefresh ==========
      if (fetchDebounceRef.current) {
        clearTimeout(fetchDebounceRef.current);
      }

      // Attendi 600ms prima di fare il fetch (raggruppa click rapidi)
      fetchDebounceRef.current = setTimeout(() => {
        if (currentViewRange) {
          // Aggiungi buffer di 7 giorni
          const bufferStart = subDays(currentViewRange.start, 7);
          const bufferEnd = addDays(currentViewRange.end, 7);

          if (pendingGoogleEnableRef.current) {
            // Se stiamo abilitando, usa forceRefresh per bypassare cooldown e usare IDs aggiornati
            fetchGoogleEvents(bufferStart, bufferEnd, {
              skipCalendarListFetch: true,
              forceRefresh: true
            });
            pendingGoogleEnableRef.current = false;
          } else {
            // Se stiamo solo disabilitando, non serve ricaricare (gli eventi vengono filtrati da getEnabledEvents)
          }
        }
        fetchDebounceRef.current = null;
        setLoadingCalendarId(null);
      }, 600);
    } else {
      // Per calendari non-Google, non serve ricaricare eventi
      // getEnabledEvents() filtra già in base allo stato enabled
    }
  };

  // Handle slot selection for activity creation
  const handleDateSelect = (start: Date, end: Date, allDay: boolean) => {
    if (onConfirmSelection) {
      if (isSidePanel) {
        // In sidepanel: selezione immediata, aggiorna subito il drawer
        onConfirmSelection(start, end);
      } else {
        // In modal: salva localmente, utente clicca Conferma
        setLocalPendingSelection({ start, end });
      }
    }
    if (onSlotSelect) {
      onSlotSelect(start, end, allDay);
    }
  };

  // Wrapper per event drop
  const handleInternalEventDrop = (_eventId: string, newStart: Date, newEnd: Date, _allDay: boolean) => {
    if (onConfirmSelection) {
      if (isSidePanel) {
        onConfirmSelection(newStart, newEnd);
      } else {
        setLocalPendingSelection({ start: newStart, end: newEnd });
      }
    }
  };

  // Wrapper per event resize
  const handleInternalEventResize = (_eventId: string, newStart: Date, newEnd: Date) => {
    if (onConfirmSelection) {
      if (isSidePanel) {
        onConfirmSelection(newStart, newEnd);
      } else {
        setLocalPendingSelection({ start: newStart, end: newEnd });
      }
    }
  };

  // Handle refresh calendar
  const handleRefreshCalendar = () => {
    clearSyncToken(); // Clear Google sync token for full refresh
    if (currentViewRange) {
      fetchEventsForDateRange(currentViewRange.start, currentViewRange.end);
      toast.success('Aggiornamento calendario in corso...');
    }
  };

  // Handle event click - open event details modal
  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setIsEventModalOpen(true);
  };

  // Navigation handlers
  const handlePrevious = () => {
    const api = calendarRef.current?.getApi();
    if (api) {
      userNavigationRef.current = true;
      toast.info('Caricamento eventi...');
      api.prev();
      const view = api.view;
      if (view) {
        const start = view.activeStart;
        const end = view.activeEnd;
        setCurrentViewRange({ start, end });
        fetchEventsForDateRange(start, end);
      }
    }
  };

  const handleNext = () => {
    const api = calendarRef.current?.getApi();
    if (api) {
      userNavigationRef.current = true;
      toast.info('Caricamento eventi...');
      api.next();
      const view = api.view;
      if (view) {
        const start = view.activeStart;
        const end = view.activeEnd;
        setCurrentViewRange({ start, end });
        fetchEventsForDateRange(start, end);
      }
    }
  };

  const handleToday = () => {
    const api = calendarRef.current?.getApi();
    if (api) {
      userNavigationRef.current = true;
      toast.info('Caricamento eventi...');
      api.today();
      const view = api.view;
      if (view) {
        const start = view.activeStart;
        const end = view.activeEnd;
        setCurrentViewRange({ start, end });
        fetchEventsForDateRange(start, end);
      }
    }
  };

  // Uscendo dal Team si ricaricano le task del periodo del calendario (il Team carica solo il suo giorno)
  const leaveTeam = () => {
    setTeamMode(false);
    if (currentViewRange) fetchEventsForDateRange(currentViewRange.start, currentViewRange.end);
    setTimeout(() => calendarRef.current?.getApi()?.updateSize(), 60);
  };

  const changeView = (viewType: string) => {
    if (teamMode) leaveTeam();
    setCurrentView(viewType);
    const api = calendarRef.current?.getApi();
    if (api) {
      userNavigationRef.current = true;
      toast.info('Caricamento eventi...');
      api.changeView(viewType);
      const view = api.view;
      if (view) {
        const start = view.activeStart;
        const end = view.activeEnd;
        setCurrentViewRange({ start, end });
        fetchEventsForDateRange(start, end);
      }
    }
  };

  // Handle delete event
  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;

    const confirmed = await confirm({
      title: 'Elimina Evento',
      message: 'Sei sicuro di voler eliminare questo evento?',
      confirmText: 'Elimina',
      cancelText: 'Annulla'
    });

    if (confirmed) {
      try {
        await deleteEvent(selectedEvent.id);
        toast.success('Evento eliminato con successo');
        setIsEventModalOpen(false);
        setSelectedEvent(null);

        // Refresh events
        if (currentViewRange) {
          fetchEventsForDateRange(currentViewRange.start, currentViewRange.end);
        }
      } catch (error) {
        console.error('Error deleting event:', error);
        toast.error('Errore durante l\'eliminazione dell\'evento');
      }
    }
  };

  // Handle update event
  const handleUpdateEvent = async (updatedData: any) => {
    try {
      await updateEvent(selectedEvent!.id, updatedData);
      toast.success('Evento aggiornato con successo');
      setShowEventForm(false);
      setShowSalesActivityForm(false);
      setIsEventModalOpen(false);
      setSelectedEvent(null);

      // Refresh events
      if (currentViewRange) {
        fetchEventsForDateRange(currentViewRange.start, currentViewRange.end);
      }
    } catch (error) {
      console.error('Error updating event:', error);
      toast.error('Errore durante l\'aggiornamento dell\'evento');
    }
  };

  // Format date time for display
  const formatDateTime = (event: CalendarEvent) => {
    if (event.all_day) {
      return `${t.allDay || 'Tutto il giorno'} - ${new Date(event.start_time).toLocaleDateString('it-IT')}`;
    }

    const startDate = new Date(event.start_time);
    const endDate = new Date(event.end_time);

    return `${startDate.toLocaleDateString('it-IT')} ${startDate.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })} - ${endDate.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
  };

  // Shared calendar content (header + calendar view)
  const isSidePanel = mode === 'sidepanel';

  const calendarContent = (
    <>
      <div className={`flex flex-col h-full ${isSidePanel ? 'bg-white dark:bg-[#191919] p-3' : 'bg-white/95 dark:bg-gray-900/95 rounded-xl p-2 sm:p-3 md:p-4'}`}>
        {/* Calendar Header Bar */}
        <div className={`${isSidePanel ? 'border-b border-[rgba(55,53,47,0.09)] dark:border-[#3F4447] pb-2 mb-2' : 'bg-white dark:bg-[#191919] border border-black/10 dark:border-white/10 rounded-xl p-2 sm:p-3 mb-2 sm:mb-3'}`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">

            {/* LEFT: Month/Year + Navigation + Today */}
            <div className="flex items-center justify-between sm:justify-start w-full sm:w-auto space-x-2 sm:space-x-4">
              <h2 className={`text-sm sm:text-lg font-medium flex items-center gap-1 sm:gap-2 ${isSidePanel ? 'text-[#37352F] dark:text-white' : 'text-black/90 dark:text-white/90'}`}>
                {currentViewRange?.start
                  ? format(currentViewRange.start, 'MMMM yyyy', { locale: it }).replace(/^./, (str) => str.toUpperCase())
                  : format(new Date(), 'MMMM yyyy', { locale: it }).replace(/^./, (str) => str.toUpperCase())
                }

                {/* Google Calendar Loading Indicator - hidden on mobile */}
                {googleLoading && (
                  <div className="hidden sm:flex items-center gap-1.5 text-xs text-blue-600 font-normal">
                    <span>Caricamento Google Calendar</span>
                    <div className="flex space-x-0.5">
                      <div className="w-1 h-1 bg-[#0f6cbd] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-1 h-1 bg-[#0f6cbd] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-1 h-1 bg-[#0f6cbd] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                )}
              </h2>

              <div className="flex items-center space-x-0.5">
                <button
                  type="button"
                  onClick={handlePrevious}
                  className="px-1.5 sm:px-2 py-1 text-xs font-medium text-[#37352F]/70 dark:text-white/70 hover:text-[#37352F] dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors"
                >
                  ←
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  className="px-1.5 sm:px-2 py-1 text-xs font-medium text-[#37352F]/70 dark:text-white/70 hover:text-[#37352F] dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors"
                >
                  →
                </button>
              </div>

              <button
                  type="button"
                onClick={handleToday}
                className="px-2 sm:px-2.5 py-1 text-xs font-medium text-[#37352F]/70 dark:text-white/70 hover:text-[#37352F] dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors"
              >
                {t.today || "Oggi"}
              </button>
            </div>

            {/* RIGHT: Search + View Selector + Toggles */}
            <div className="flex items-center justify-between sm:justify-end w-full sm:w-auto space-x-1 sm:space-x-2">

              {/* Search - nascosto su mobile per risparmiare spazio */}
              {!isSidePanel && (
                <div className="hidden sm:block">
                  {isSearching ? (
                    <div className="flex items-center">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Cerca eventi..."
                        className="px-2 py-1 text-xs border border-[rgba(55,53,47,0.09)] rounded focus:outline-none focus:ring-1 focus:ring-[#2383E2]"
                        autoFocus
                      />
                      <button
                  type="button"
                        onClick={() => {
                          setIsSearching(false);
                          setSearchQuery('');
                        }}
                        className="ml-1 px-2 py-1 text-xs text-[#37352F]/70 hover:text-[#37352F]"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <button
                  type="button"
                      onClick={() => setIsSearching(true)}
                      className="p-1.5 text-[#37352F]/70 hover:text-[#37352F] hover:bg-black/5 rounded transition-colors"
                    >
                      <Search className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              )}

              {/* View Selector */}
              <div className={`flex p-0.5 rounded-md ${isSidePanel ? 'bg-[#F7F7F5] dark:bg-[#2F3437]' : 'bg-[#F7F7F5] dark:bg-white/5 border border-black/10 dark:border-white/10'}`}>
                {!isSidePanel && (
                  <button
                  type="button"
                    onClick={() => changeView('dayGridMonth')}
                    className={`px-1.5 sm:px-2 py-1 text-xs font-medium rounded transition-colors ${
                      currentView === 'dayGridMonth'
                        ? 'bg-[#2383E2] text-white shadow-sm'
                        : 'text-[#787774] dark:text-white/70 hover:text-[#37352F] dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5'
                    }`}
                  >
                    <span className="sm:hidden">M</span>
                    <span className="hidden sm:inline">Mese</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => changeView('timeGridWeek')}
                  className={`px-1.5 sm:px-2 py-1 text-xs font-medium rounded transition-colors ${
                    currentView === 'timeGridWeek'
                      ? 'bg-[#2383E2] text-white shadow-sm'
                      : 'text-[#787774] dark:text-white/70 hover:text-[#37352F] dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5'
                  }`}
                >
                  <span className="sm:hidden">S</span>
                  <span className="hidden sm:inline">Settimana</span>
                </button>
                <button
                  type="button"
                  onClick={() => changeView('timeGridThreeDay')}
                  className={`px-1.5 sm:px-2 py-1 text-xs font-medium rounded transition-colors ${
                    currentView === 'timeGridThreeDay'
                      ? 'bg-[#2383E2] text-white shadow-sm'
                      : 'text-[#787774] dark:text-white/70 hover:text-[#37352F] dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5'
                  }`}
                >
                  <span className="sm:hidden">3G</span>
                  <span className="hidden sm:inline">3 Giorni</span>
                </button>
                <button
                  type="button"
                  onClick={() => changeView('timeGridDay')}
                  className={`px-1.5 sm:px-2 py-1 text-xs font-medium rounded transition-colors ${
                    currentView === 'timeGridDay'
                      ? 'bg-[#2383E2] text-white shadow-sm'
                      : 'text-[#787774] dark:text-white/70 hover:text-[#37352F] dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5'
                  }`}
                >
                  <span className="sm:hidden">G</span>
                  <span className="hidden sm:inline">Giorno</span>
                </button>
                {canShowTeam && (
                  <button
                    type="button"
                    onClick={() => (teamMode ? leaveTeam() : setTeamMode(true))}
                    aria-pressed={teamMode}
                    title="Vedi quando sono liberi i colleghi"
                    className={`px-1.5 sm:px-2 py-1 text-xs font-medium rounded transition-colors ${
                      teamMode
                        ? 'bg-[#2383E2] text-white shadow-sm'
                        : 'text-[#787774] dark:text-white/70 hover:text-[#37352F] dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5'
                    }`}
                  >
                    Team
                  </button>
                )}
              </div>

              {/* Calendar Sources Dropdown */}
              <CalendarSourcesDropdown
                calendarSources={calendarSources}
                onToggle={handleCalendarSourceToggle}
                isLoading={googleLoading}
                loadingCalendarId={loadingCalendarId}
              />

              {/* Sidebar Toggle - nascosto su mobile e in sidepanel */}
              {!isSidePanel && (
                <button
                  type="button"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="hidden sm:block p-1.5 text-[#37352F]/70 dark:text-white/70 hover:text-[#37352F] dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors"
                >
                  <SidebarIcon className="h-3.5 w-3.5" />
                </button>
              )}

              {/* Close Button */}
              <button
                  type="button"
                onClick={onClose}
                className="p-1.5 text-[#37352F]/70 dark:text-white/70 hover:text-[#37352F] dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors"
                aria-label="Chiudi"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Calendar View — wrapper `cal-modal-fc` scopes the Notion-like overrides
            (vedi calendar-modal.css) SENZA toccare CalendarView/planning */}
        {teamMode && (
          <div className="flex-1 min-h-0 overflow-hidden" data-testid="calendar-modal-team">
            <TeamAvailabilityView compact initialDay={slotDateTime || undefined} defaultDuration={slotDuration || 60} onPickSlot={onTeamPick} />
          </div>
        )}
        <div className={`cal-modal-fc flex-1 overflow-hidden ${teamMode ? 'hidden' : ''}`}>
          <CalendarView
            events={memoizedEvents}
            onEventClick={handleEventClick}
            onViewChange={handleViewChange}
            calendarRef={calendarRef}
            isLoading={loading || salesActivitiesLoading || projectTasksLoading}
            googleLoading={googleLoading}
            filterText={searchQuery}
            sidebarOpen={isSidePanel ? false : sidebarOpen}
            onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
            t={t}
            onDateSelect={(onSlotSelect || onConfirmSelection) ? handleDateSelect : undefined}
            onEventDrop={onConfirmSelection ? handleInternalEventDrop : onEventDrop}
            onEventResize={onConfirmSelection ? handleInternalEventResize : onEventResize}
            highlightEventId={highlightEventId}
            hideEventId={hideEventId}
          />
        </div>
      </div>

      {/* Event Details Drawer */}
      <CalendarEventDrawer
        isOpen={isEventModalOpen && !!selectedEvent}
        onClose={() => {
          setIsEventModalOpen(false);
          setSelectedEvent(null);
        }}
        event={selectedEvent}
        onEdit={() => {
          setIsEventModalOpen(false);
          if (selectedEvent?.source === 'sales_activities' ||
              selectedEvent?.event_type === 'sales_activity') {
            setShowSalesActivityForm(true);
          } else {
            setShowEventForm(true);
          }
        }}
        onDelete={handleDeleteEvent}
        canEdit={canEditEvent}
        canDelete={canDeleteEvent}
        translations={t}
      />

      {/* Event Form Drawer */}
      <EventFormDrawer
        isOpen={showEventForm && !!selectedEvent}
        onClose={() => setShowEventForm(false)}
        event={selectedEvent}
        onSave={handleUpdateEvent}
        translations={t}
      />

      {/* Edit Sales Activity Form */}
      {showSalesActivityForm && selectedEvent && selectedEvent.deal_id && (
        <ResponsiveModal
          isOpen={showSalesActivityForm}
          onClose={() => setShowSalesActivityForm(false)}
          title="Modifica Attività"
          size="lg"
          mobileHeight="full"
        >
          <DealActivityForm
            activityId={selectedEvent.id}
            dealId={selectedEvent.deal_id}
            editingActivity={selectedEvent}
            onSuccess={() => {
              setShowSalesActivityForm(false);
              setSelectedEvent(null);
              if (currentViewRange) {
                fetchEventsForDateRange(currentViewRange.start, currentViewRange.end);
              }
            }}
            onCancel={() => setShowSalesActivityForm(false)}
          />
        </ResponsiveModal>
      )}

      {/* Pulsante Conferma - solo in modal mode (in sidepanel la selezione è immediata) */}
      {!isSidePanel && localPendingSelection && onConfirmSelection && (
        <div className="absolute bottom-6 right-6 z-50">
          <button
                  type="button"
            onClick={() => {
              if (localPendingSelection) {
                onConfirmSelection(localPendingSelection.start, localPendingSelection.end);
                setLocalPendingSelection(null);
              }
            }}
            className="px-5 py-2.5 rounded-md shadow-lg font-medium transition-colors bg-[#37352F] text-white hover:bg-[#2f2d28]"
          >
            Conferma
          </button>
        </div>
      )}
    </>
  );

  // === SIDE PANEL MODE ===
  if (isSidePanel) {
    return (
      <AnimatePresence>
        {isOpen && (
          /* Drawer full-height dal bordo sinistro dello schermo (copre anche la sidebar)
             fino al bordo del drawer attività a destra (larghezze allineate: lg 1/2,
             xl 1/3). Nessun backdrop: il drawer a destra resta visibile e interattivo. */
          <motion.div
            initial={{ x: '-110%' }}
            animate={{ x: 0 }}
            exit={{ x: '-110%' }}
            transition={{ type: 'tween', duration: 0.3, ease: 'easeOut' }}
            className="fixed inset-y-0 left-0 right-0 lg:right-[50%] xl:right-[33.33%] z-[10001] bg-white dark:bg-[#191919] border-r border-[rgba(55,53,47,0.09)] dark:border-[#3F4447] shadow-[4px_0_16px_rgba(0,0,0,0.10)]"
          >
            {calendarContent}
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  // === MODAL MODE (default) ===
  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size={size === 'full' ? 'fullscreen' : 'xl'}
      mobileHeight="full"
    >
      <div className="flex flex-col h-[calc(100vh-80px)] sm:h-[calc(100vh-120px)] md:h-[calc(100vh-200px)] min-h-[300px] sm:min-h-[400px] md:min-h-[600px]">
        {calendarContent}
      </div>
    </ResponsiveModal>
  );
}
